import React, { createRef, useState, useCallback } from 'react';
import { PhotoCamera as PhotoCameraIcon } from '@mui/icons-material';
import { ALLOWED_FILE_EXTENSIONS_QUERY } from '../queries';
import { OCR_UPLOAD_MUTATION } from '../mutations';
import { useMutation, useConditionalSubscription } from 'features/common/security/hooks';
import { OCR_ID_CARD_PROCESSED } from '../subscriptions';
import { useSimpleError, useQueryWithSimpleErrorHandling } from 'hooks';
import PropTypes from 'prop-types';
import { IconButton } from '@totalsoft/rocket-ui';

const OCRButton = ({ tooltip, disabled, context }) => {
  const [fileName, setFileName] = useState(undefined);
  const [allowedFileExtensions, setAllowedFileExtensions] = useState([]);
  const fileRef = createRef();
  const { metadata, events } = context;
  const { onStarting, onCompleted, onError } = events;
  const showError = useSimpleError();

  useQueryWithSimpleErrorHandling(ALLOWED_FILE_EXTENSIONS_QUERY, {
    onCompleted: data => {
      setAllowedFileExtensions(data.allowedFileExtensions);
    }
  });

  const [ocrUploadMutation] = useMutation(OCR_UPLOAD_MUTATION, {
    onError: error => {
      showError(error);
      onError({ processingOCR: false, error });
    }
  });

  const uploadFile = useCallback(
    file => {
      if (!file) return;

      setFileName(file.name);
      onStarting && onStarting(file);

      ocrUploadMutation({ variables: { file } });
      onStarting({ processingOCR: true });
    },
    [ocrUploadMutation, onStarting]
  );

  const handleSelection = event => {
    if (event.target.files.length > 0) {
      const file = event.target.files[0];
      uploadFile(file);
    }
  };

  const handleClick = () => {
    fileRef.current.value = '';
    fileRef.current.click();
  };

  useConditionalSubscription(OCR_ID_CARD_PROCESSED, {
    onData: ({ data: { data } }) => {
      onCompleted({ processingOCR: false, ocrData: data.ocrIdCardProcessed, fileName, ...metadata });
    }
  });

  return (
    <>
      <input type='file' accept={allowedFileExtensions} style={{ display: 'none' }} ref={fileRef} onChange={handleSelection} />
      <IconButton id='ocr-button' size='small' variant='text' disabled={disabled} tooltip={tooltip} onClick={handleClick}>
        <PhotoCameraIcon />
      </IconButton>
    </>
  );
};

OCRButton.propTypes = {
  tooltip: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  context: PropTypes.shape({
    metadata: PropTypes.object,
    events: PropTypes.shape({
      onStarting: PropTypes.func,
      onCompleted: PropTypes.func.isRequired,
      onError: PropTypes.func.isRequired
    })
  })
};

export default OCRButton;
